<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    @click="handleClick"
    :type="type"
  >
    <span v-if="loading" class="btn-spinner">
      <svg class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" stroke-opacity="0.3"/>
        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
      </svg>
    </span>
    <span v-if="iconLeft && !loading" class="btn-icon-left">
      <slot name="icon-left"></slot>
    </span>
    <span class="btn-content">
      <slot></slot>
    </span>
    <span v-if="iconRight && !loading" class="btn-icon-right">
      <slot name="icon-right"></slot>
    </span>
  </button>
</template>

<script>
export default {
  name: 'BertButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'accent', 'success', 'warning', 'error', 'neutral', 'ghost', 'outline', 'white'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    iconLeft: {
      type: Boolean,
      default: false
    },
    iconRight: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    }
  },
  computed: {
    buttonClasses() {
      return [
        'bert-btn',
        `bert-btn--${this.variant}`,
        `bert-btn--${this.size}`,
        {
          'bert-btn--disabled': this.disabled,
          'bert-btn--loading': this.loading,
          'bert-btn--full-width': this.fullWidth
        }
      ]
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event)
      }
    }
  }
}
</script>

<style scoped>
.bert-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-family: var(--font-family-sans);
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--transition-base);
  text-decoration: none;
  position: relative;
  overflow: hidden;
  margin: 0;
}

.bert-btn:focus {
  outline: 2px solid var(--primary-500);
  outline-offset: 2px;
}

/* Sizes */
.bert-btn--xs {
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-xs);
  border-radius: var(--radius-full);
  min-height: 24px;
}

.bert-btn--sm {
  padding: var(--space-2) var(--space-4);
  font-size: var(--text-sm);
  border-radius: var(--radius-full);
  min-height: 32px;
}

.bert-btn--md {
  padding: var(--space-3) var(--space-6);
  font-size: var(--text-base);
  border-radius: var(--radius-full);
  min-height: 40px;
}

.bert-btn--lg {
  padding: var(--space-4) var(--space-8);
  font-size: var(--text-lg);
  border-radius: var(--radius-full);
  min-height: 48px;
}

.bert-btn--xl {
  padding: var(--space-5) var(--space-10);
  font-size: var(--text-xl);
  border-radius: var(--radius-full);
  min-height: 56px;
}

/* Variants */
.bert-btn--primary {
  background-color: var(--yellow-main);
  color: white;
  border-color: var(--yellow-main);
}

.bert-btn--primary:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--yellow-dark);
  border-color: var(--yellow-dark);
}

.bert-btn--secondary {
  background-color: var(--blue-medium);
  color: white;
  border-color: var(--blue-medium);
}

.bert-btn--secondary:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--blue-medium);
  border-color: var(--blue-medium);
  opacity: 0.9;
}

.bert-btn--accent {
  background-color: var(--purple-medium);
  color: white;
  border-color: var(--purple-medium);
}

.bert-btn--accent:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--purple-medium);
  border-color: var(--purple-medium);
  opacity: 0.9;
}

.bert-btn--success {
  background-color: var(--green-medium);
  color: white;
  border-color: var(--green-medium);
}

.bert-btn--success:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--green-medium);
  border-color: var(--green-medium);
  opacity: 0.9;
}

.bert-btn--warning {
  background-color: var(--yellow-soft);
  color: white;
  border-color: var(--yellow-soft);
}

.bert-btn--warning:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--yellow-radial);
  border-color: var(--yellow-radial);
}

.bert-btn--error {
  background-color: var(--red-medium);
  color: white;
  border-color: var(--red-medium);
}

.bert-btn--error:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--red-medium);
  border-color: var(--red-medium);
  opacity: 0.9;
}

.bert-btn--neutral {
  background-color: var(--text-soft);
  color: var(--text-dark);
  border-color: var(--text-medium);
}

.bert-btn--neutral:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--text-medium);
  border-color: var(--text-dark-grey);
}

.bert-btn--ghost {
  background-color: transparent;
  color: var(--yellow-main);
  border-color: transparent;
}

.bert-btn--ghost:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--yellow-ultrasoft);
  color: var(--yellow-dark);
}

.bert-btn--outline {
  background-color: white;
  color: var(--yellow-main);
  border-color: var(--yellow-main);
}

.bert-btn--outline:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--yellow-main);
  color: white;
}

.bert-btn--white {
  background-color: var(--basic-white);
  color: var(--text-dark);
  border-color: var(--basic-white);
}

.bert-btn--white:hover:not(.bert-btn--disabled):not(.bert-btn--loading) {
  background-color: var(--text-ultrasoft);
  border-color: var(--text-ultrasoft);
}

/* States */
.bert-btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.bert-btn--loading {
  cursor: not-allowed;
}

.bert-btn--full-width {
  width: 100%;
}

/* Spinner animation */
.btn-spinner {
  display: flex;
  align-items: center;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.btn-content {
  display: flex;
  align-items: center;
}

.btn-icon-left,
.btn-icon-right {
  display: flex;
  align-items: center;
}
</style>